import React, { useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import { Upload, Download, Image as ImageIcon, Share2, MessageCircle, Instagram } from "lucide-react";
import { club } from "../mock";

// Client-side branded tryout card rendered to a canvas.
// Player can add their own photo, then download / share to Instagram.
export default function ShareableTryoutCard({ playerName }) {
  const canvasRef = useRef(null);
  const [photo, setPhoto] = useState(null); // HTMLImageElement
  const [crest, setCrest] = useState(null);
  const [qr, setQr] = useState(null); // HTMLImageElement of the QR code
  const [ready, setReady] = useState(false);
  const [template, setTemplate] = useState("crest"); // "crest" | "photo"
  const usePhoto = template === "photo" && !!photo;

  // Preload the crest once.
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => { setCrest(img); setReady(true); };
    img.onerror = () => setReady(true);
    img.src = club.crest;
  }, []);

  // Generate a QR code linking to the public registration section.
  useEffect(() => {
    const url = `${window.location.origin}/#register`;
    QRCode.toDataURL(url, { margin: 1, width: 240, color: { dark: "#0a0a0b", light: "#ffffff" } })
      .then((dataUrl) => {
        const img = new Image();
        img.onload = () => setQr(img);
        img.src = dataUrl;
      })
      .catch(() => {});
  }, []);

  const draw = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const W = 1080, H = 1350;
    const ctx = canvas.getContext("2d");

    // Background
    ctx.fillStyle = "#0a0a0b";
    ctx.fillRect(0, 0, W, H);
    const grad = ctx.createRadialGradient(W / 2, H * 0.32, 80, W / 2, H * 0.32, 900);
    grad.addColorStop(0, "rgba(229,184,51,0.20)");
    grad.addColorStop(1, "rgba(10,10,11,0)");
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, W, H);

    // Gold border frame
    ctx.strokeStyle = "#e5b833";
    ctx.lineWidth = 8;
    ctx.strokeRect(36, 36, W - 72, H - 72);

    // Optional player photo in a gold ring
    const cx = W / 2, cy = 470, r = 250;
    if (usePhoto) {
      ctx.save();
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.closePath();
      ctx.clip();
      const ar = photo.width / photo.height;
      let dw = r * 2, dh = r * 2, dx = cx - r, dy = cy - r;
      if (ar > 1) { dw = dh * ar; dx = cx - dw / 2; } else { dh = dw / ar; dy = cy - dh / 2; }
      ctx.drawImage(photo, dx, dy, dw, dh);
      ctx.restore();
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.lineWidth = 10;
      ctx.strokeStyle = "#e5b833";
      ctx.stroke();
    } else if (crest) {
      const cw = 300, chh = crest.height * (300 / crest.width);
      ctx.drawImage(crest, cx - cw / 2, cy - chh / 2, cw, chh);
    }

    // Small crest above the text if a photo is present
    if (usePhoto && crest) {
      const cw = 140, chh = crest.height * (140 / crest.width);
      ctx.drawImage(crest, cx - cw / 2, cy + r + 34, cw, chh);
    }

    // Text
    ctx.textAlign = "center";
    ctx.fillStyle = "#e5b833";
    ctx.font = "700 34px Oswald, sans-serif";
    ctx.fillText("ATLÉTICO SOUTH FLORIDA", W / 2, usePhoto ? 950 : 880);

    ctx.fillStyle = "#ffffff";
    ctx.font = "800 84px 'Anton', Oswald, sans-serif";
    const name = (playerName || "OUR NEW PLAYER").toUpperCase();
    ctx.fillText(name, W / 2, usePhoto ? 1050 : 990);

    ctx.fillStyle = "rgba(255,255,255,0.82)";
    ctx.font = "400 40px Oswald, sans-serif";
    ctx.fillText("I'M TRYING OUT FOR THE CREST", W / 2, usePhoto ? 1130 : 1080);

    ctx.fillStyle = "#e5b833";
    ctx.font = "700 30px Oswald, sans-serif";
    ctx.fillText("#DRIVENTOCOMPETE", W / 2, usePhoto ? 1210 : 1170);

    // QR code linking to the registration page (bottom-right)
    if (qr) {
      const qs = 120;
      const qx = W - 72 - qs - 12;
      const qy = H - 72 - qs - 12;
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(qx - 6, qy - 6, qs + 12, qs + 12);
      ctx.drawImage(qr, qx, qy, qs, qs);
      ctx.fillStyle = "rgba(255,255,255,0.72)";
      ctx.font = "600 19px Oswald, sans-serif";
      ctx.textAlign = "center";
      ctx.fillText("SCAN TO REGISTER", qx + qs / 2, qy - 14);
    }
  };

  useEffect(() => { if (ready) draw(); /* eslint-disable-next-line */ }, [ready, photo, playerName, template, qr]);

  const onPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => setPhoto(img);
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const download = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const url = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url;
    a.download = "atletico-tryout-card.png";
    a.click();
  };

  const shareText = `I'm trying out for Atlético South Florida! Register at ${window.location.origin}/#register ${club.instagram}`;

  const getBlob = () =>
    new Promise((resolve) => {
      const canvas = canvasRef.current;
      if (!canvas) return resolve(null);
      canvas.toBlob((b) => resolve(b), "image/png");
    });

  // One-tap native share sheet (Instagram, WhatsApp, Messages…) with the card image attached.
  const shareNative = async () => {
    const blob = await getBlob();
    if (blob && navigator.canShare) {
      const file = new File([blob], "atletico-tryout-card.png", { type: "image/png" });
      if (navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], text: shareText, title: "Atlético South Florida" });
          return;
        } catch (e) {
          if (e && e.name === "AbortError") return;
        }
      }
    }
    // Fallback (desktop / unsupported): download so they can attach it manually.
    download();
  };

  const shareWhatsApp = () => {
    download();
    window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, "_blank", "noopener");
  };

  const shareInstagram = () => {
    download();
    window.open("https://www.instagram.com/", "_blank", "noopener");
  };

  return (
    <div className="mt-10" data-testid="shareable-card">
      <p className="cond-font uppercase tracking-widest text-gold text-sm mb-4">Share your tryout card</p>

      {/* Template picker */}
      <div className="inline-flex rounded-md border border-white/15 overflow-hidden mb-5" data-testid="card-template-toggle">
        {[
          { id: "crest", label: "Crest Only" },
          { id: "photo", label: "With Photo" },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTemplate(t.id)}
            className={`cond-font uppercase tracking-widest text-xs px-5 py-2.5 transition-colors ${
              template === t.id ? "bg-gold text-black" : "bg-transparent text-white/60 hover:text-white"
            }`}
            data-testid={`card-template-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="mx-auto max-w-[320px] rounded-xl overflow-hidden border border-white/15 shadow-2xl">
        <canvas ref={canvasRef} width={1080} height={1350} className="w-full h-auto block" data-testid="tryout-card-canvas" />
      </div>
      <div className="flex flex-wrap items-center justify-center gap-3 mt-5">
        {template === "photo" && (
          <label
            className="cursor-pointer inline-flex items-center gap-2 border border-white/25 text-white cond-font uppercase tracking-widest text-xs px-5 py-3 rounded-sm hover:border-gold hover:text-gold transition-colors"
            data-testid="card-add-photo-btn"
          >
            <Upload size={15} /> {photo ? "Change Photo" : "Add Your Photo"}
            <input type="file" accept="image/*" className="hidden" onChange={onPhoto} data-testid="card-photo-input" />
          </label>
        )}
        {template === "photo" && photo && (
          <button
            onClick={() => setPhoto(null)}
            className="inline-flex items-center gap-2 border border-white/15 text-white/60 cond-font uppercase tracking-widest text-xs px-5 py-3 rounded-sm hover:text-white transition-colors"
            data-testid="card-remove-photo-btn"
          >
            <ImageIcon size={15} /> Remove
          </button>
        )}
        <button
          onClick={shareNative}
          className="inline-flex items-center gap-2 bg-gold text-black cond-font uppercase tracking-widest text-xs font-semibold px-5 py-3 rounded-sm hover:bg-gold-light transition-colors"
          data-testid="card-share-btn"
        >
          <Share2 size={15} /> Share
        </button>
        <button
          onClick={shareWhatsApp}
          className="inline-flex items-center gap-2 border border-white/25 text-white cond-font uppercase tracking-widest text-xs px-5 py-3 rounded-sm hover:border-gold hover:text-gold transition-colors"
          data-testid="card-whatsapp-btn"
        >
          <MessageCircle size={15} /> WhatsApp
        </button>
        <button
          onClick={shareInstagram}
          className="inline-flex items-center gap-2 border border-white/25 text-white cond-font uppercase tracking-widest text-xs px-5 py-3 rounded-sm hover:border-gold hover:text-gold transition-colors"
          data-testid="card-instagram-btn"
        >
          <Instagram size={15} /> Instagram
        </button>
        <button
          onClick={download}
          className="inline-flex items-center gap-2 border border-white/15 text-white/60 cond-font uppercase tracking-widest text-xs px-5 py-3 rounded-sm hover:text-white transition-colors"
          data-testid="card-download-btn"
        >
          <Download size={15} /> Download
        </button>
      </div>
      <p className="text-white/40 text-xs font-light text-center mt-3">
        {template === "photo" && !photo
          ? "Add a photo to personalise your card, then download & share."
          : `Save the image, then post it to your Instagram story and tag ${club.instagram}.`}
      </p>
    </div>
  );
}
